import { styled } from "@linaria/react";
import { CheckIcon, MinusIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import FlexWrapper, { AlignItems } from "../containers/FlexWrapper";
import Icon, { IconVariant, IconWeight } from "../icons/Icon";
import RequiredMarker from "../text/RequiredMarker";
import Text, { TextVariant } from "../text/Text";

export enum CheckboxInputVariant {
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
}

export enum CheckboxInputSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
}

export interface CheckboxInputProps {
  variant?: CheckboxInputVariant;
  size?: CheckboxInputSize;
  isChecked: boolean;
  isIndeterminate?: boolean;
  label?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  onChange: (checked: boolean) => void;
}

const CheckboxContainer = styled.div<{ $isDisabled: boolean; $isRequired: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
`;

const CheckboxWrapper = withTheme(styled.div<
  PropsWithTheme<{
    $variant: CheckboxInputVariant;
    $size: CheckboxInputSize;
    $isChecked: boolean;
    $isIndeterminate: boolean;
    $isDisabled: boolean;
  }>
>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${({ $size }) => {
    return match($size)
      .with(CheckboxInputSize.SMALL, () => "12px")
      .with(CheckboxInputSize.MEDIUM, () => "16px")
      .exhaustive();
  }};
  height: ${({ $size }) => {
    return match($size)
      .with(CheckboxInputSize.SMALL, () => "12px")
      .with(CheckboxInputSize.MEDIUM, () => "16px")
      .exhaustive();
  }};
  border-radius: 4px;
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};

  background-color: ${({ theme, $isChecked, $isIndeterminate }) =>
    $isChecked || $isIndeterminate
      ? theme.color.background.primaryAlt
      : theme.color.background.tertiary};

  transition:
    border-color 0.075s ease-in-out,
    background-color 0.075s ease-in-out;

  &:hover {
    border-color: ${({ theme, $isDisabled, $variant }) => {
      return match($variant)
        .with(CheckboxInputVariant.PRIMARY, () =>
          $isDisabled ? theme.color.border.primary : theme.color.border.primary,
        )
        .with(CheckboxInputVariant.SECONDARY, () =>
          $isDisabled ? theme.color.border.primary : theme.color.border.primary,
        )
        .exhaustive();
    }};
  }
`);

const CHECKBOX_INPUT_SIZE_TO_ICON_SIZE_MAP: Record<CheckboxInputSize, number> = {
  [CheckboxInputSize.SMALL]: 10,
  [CheckboxInputSize.MEDIUM]: 12,
};

const CheckboxInput = ({
  variant = CheckboxInputVariant.PRIMARY,
  size = CheckboxInputSize.MEDIUM,
  isChecked,
  isIndeterminate = false,
  label,
  isRequired = false,
  isDisabled = false,
  onChange,
}: CheckboxInputProps) => {
  const handleClick = () => {
    if (!isDisabled) {
      onChange(!isChecked);
    }
  };

  const renderIcon = () => {
    if (isIndeterminate) {
      return (
        <Icon
          component={MinusIcon}
          size={CHECKBOX_INPUT_SIZE_TO_ICON_SIZE_MAP[size]}
          weight={IconWeight.BOLD}
          variant={IconVariant.PRIMARY_ALT}
        />
      );
    }
    if (isChecked) {
      return (
        <Icon
          component={CheckIcon}
          size={CHECKBOX_INPUT_SIZE_TO_ICON_SIZE_MAP[size]}
          weight={IconWeight.BOLD}
          variant={IconVariant.PRIMARY_ALT}
        />
      );
    }
    return null;
  };

  return (
    <CheckboxContainer $isDisabled={isDisabled} $isRequired={isRequired} onClick={handleClick}>
      <CheckboxWrapper
        $variant={variant}
        $size={size}
        $isChecked={isChecked}
        $isIndeterminate={isIndeterminate}
        $isDisabled={isDisabled}
      >
        {renderIcon()}
      </CheckboxWrapper>
      {label && (
        <FlexWrapper alignItems={AlignItems.CENTER} gap={1}>
          <Text variant={TextVariant.PRIMARY}>{label}</Text>
          {isRequired && <RequiredMarker />}
        </FlexWrapper>
      )}
    </CheckboxContainer>
  );
};

export default CheckboxInput;
